/*
Copyright (C) 2024 Laboratorio Visao Robotica e Imagem

Departamento de Informatica - Universidade Federal do Parana - VRI/UFPR

This file is part of CienciaNaEscola. CienciaNaEscola is free software: you can redistribute it and/or modify it under the terms of the GNU
General Public License as published by the Free Software Foundation, either version 3 of the License, or (at your option) any later version.
CienciaNaEscola is distributed in the hope that it will be useful, but WITHOUT ANY WARRANTY; without even the implied warranty of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the GNU General Public License for more details. You should have received a copy
of the GNU General Public License along with CienciaNaEscola.  If not, see <https://www.gnu.org/licenses/>
*/

import { useEffect, useState, useCallback } from 'react';
import MarkdownText from '../../MarkdownText';
import Gallery from '../../Gallery';

const styles = `
    #customRange {
        -webkit-appearance: none;
        appearance: none;
        background: transparent;
        cursor: pointer;
        width: 15rem;
    }

    #customRange::-webkit-slider-runnable-track {
        height: 0.5rem;
        border-radius: 5px;
    }

    #customRange::-webkit-slider-thumb {
        -webkit-appearance: none;
        height: 25px;
        width: 8px;
        margin-top: -8px;
        border-radius: 5px;
        background-color: #535353;
    }

    .range-subtitle span {
        width: 40px;
    }

    .bg-selected {
        background-color: #aad39094;
    }
`;

/**
 * Componente responsável por exibir um controle de range com valores mínimos, máximos e passo configurável.
 * @param {Object} props - Propriedades do componente.
 * @param {Function} props.onAnswerChange - Função chamada quando a resposta é alterada.
 * @param {Object} props.item - Objeto representando o item.
 * @param {Object} props.answer - Objeto contendo a resposta.
 * @param {boolean} props.disabled - Define se a interação com o componente está desabilitada.
 */
function RangeInput(props) {
    const { onAnswerChange, answer, item, galleryModalRef, disabled } = props;
    const [min, setMin] = useState(0);
    const [max, setMax] = useState(10);
    const [step, setStep] = useState(1);
    const [hasUpdated, setHasUpdated] = useState(false);

    /** Atualiza a resposta com o novo valor do range. */
    const updateAnswer = useCallback(
        (newAnswer) => {
            onAnswerChange(answer.group, item.id, 'ITEM', newAnswer);
        },
        [onAnswerChange, answer.group, item.id]
    );

    useEffect(() => {
        if (!hasUpdated && item) {
            let newMin = 0;
            let newMax = 10;
            let newStep = 1;

            item.itemValidations?.forEach((v) => {
                const val = parseInt(v.argument);
                switch (v.type) {
                    case 'MIN':
                        newMin = val > 800000 ? 800000 : val;
                        break;
                    case 'MAX':
                        newMax = val > 1000000 ? 1000000 : val;
                        break;
                    case 'STEP':
                        newStep = val;
                        break;
                    default:
                        break;
                }
            });

            setMin(newMin);
            setMax(newMax);
            setStep(newStep);
            setHasUpdated(true);
        }
    }, [hasUpdated, item]);

    useEffect(() => {
        // Verificamos se o valor atual é nulo, indefinido ou inválido
        const currentValue = answer.text;
        const isEmpty = currentValue === undefined || currentValue === null || currentValue === '';
        const isInvalid = !isEmpty && isNaN(parseFloat(currentValue));

        // Apenas inicializa após carregar os metadados (hasUpdated)
        if (hasUpdated && (isEmpty || isInvalid)) {
            updateAnswer({ ...answer, text: String(min) });
        }
    }, [min, hasUpdated, answer.text, updateAnswer, answer]);

    /** Esilização do controle do range. */
    const rangeStyle = {
        background: '#AAD390',
        borderRadius: '5px',
        cursor: disabled ? 'not-allowed' : 'pointer',
    };

    return (
        <div className="rounded-4 shadow bg-white p-3">
            <div className="row g-0">
                <label htmlFor="customRange">
                    <MarkdownText text={item.text} />
                </label>
            </div>
            <Gallery className="mb-3" item={item} galleryModalRef={galleryModalRef} />
            <div className="row g-0">
                <input
                    type="range"
                    className="w-100"
                    min={min}
                    max={max}
                    step={step}
                    id="customRange"
                    onChange={(e) => updateAnswer({ ...answer, text: String(e.target.value) })}
                    style={rangeStyle}
                    value={parseFloat(answer.text) || min}
                    disabled={disabled}
                />
            </div>
            <div className="range-subtitle d-flex justify-content-between pt-2">
                <span className={`${parseFloat(answer.text) === parseFloat(min) ? 'fw-medium fs-5' : 'fw-normal fs-6'} w-25`}>{min}</span>
                {!disabled && <span className={'text-center fw-medium bg-selected rounded fs-5 w-auto px-4'}>{answer.text}</span>}
                <span className={`${parseFloat(answer.text) === parseFloat(max) ? 'fw-medium fs-5' : 'fw-normal fs-6'} text-end w-25`}>
                    {max}
                </span>
            </div>
            <style>{styles}</style>
        </div>
    );
}

export default RangeInput;
